// PromotionsTab.js - Promotions configuration
import {
  React,
  useEffect,
  useState,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC
} from './PromoFinancingCommon';
import { useAppFlags } from '../utils/AppFlagsContext';

const PromotionsTab = () => {
  const { flags, refresh: refreshFlags } = useAppFlags();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [filterModel, setFilterModel] = useState({ items: [] });

  const fetchData = () => {
    setLoading(true);
    authFetch('/promotions/')
      .then(data => { setRows(data); setLoading(false); })
      .catch(err => { console.error('Failed to fetch:', err); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/promotions/${editData.id}`, formData, 'PUT');
        setRows(prevRows => prevRows.map(row => row.id === editData.id ? { ...formData, id: editData.id } : row));
        if (selectedRow?.id === editData.id) {
          setSelectedRow({ ...formData, id: editData.id });
        }
      } else {
        updatedRow = await authFetchRequest('/promotions/', formData, 'POST');
        if (updatedRow && updatedRow.id) {
          setRows(prevRows => [...prevRows, updatedRow]);
        } else {
          fetchData();
        }
      }
      setOpenDialog(false);
      setEditData(null);
    } catch (err) {
      console.error('Save failed:', err);
      await alertC('Failed to save promotion');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this promotion?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/promotions/${selectedRow.id}`, null, 'DELETE');
      setRows(prevRows => prevRows.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'description', headerName: 'Description', width: 200 },
    { field: 'available_to', headerName: 'Available To', width: 150 },
    { field: 'visible', headerName: 'Visible', width: 80, renderCell: (params) => params.value ? 'Yes' : 'No' },
    { field: 'type', headerName: 'Type', width: 100 },
    { field: 'discount_type', headerName: 'Discount Type', width: 120 },
    { field: 'discount', headerName: 'Discount', width: 80 },
    { field: 'start_date', headerName: 'Start Date', width: 120 },
    { field: 'expiration_date', headerName: 'Expiration Date', width: 120 },
    { field: 'applies_to', headerName: 'Applies To', width: 150 }
  ];

  // Toggle the promotions_enabled feature flag (AppSettings on the backend).
  // When OFF, the Promo/Financing column in the Quote dialog is hidden,
  // and (if Financing is also OFF) the Quote button becomes "Entire Quote".
  const handleToggleEnabled = async (e) => {
    const enabled = e.target.checked;
    try {
      await authFetchRequest('/settings/promotions_enabled', { enabled }, 'PUT');
      refreshFlags();
    } catch (err) {
      alertC('Failed to update promotions feature flag: ' + (err?.message || err));
    }
  };

  // While the feature is OFF, lock the data management below so admins
  // can't edit records for a feature that won't be visible anywhere.
  const featureOn = !!flags.promotions_enabled;
  const disabledStyle = featureOn
    ? {}
    : { opacity: 0.45, pointerEvents: 'none' };

  return (
    <Box>
      <FormControlLabel
        sx={{ mb: 1 }}
        control={
          <Checkbox
            checked={featureOn}
            onChange={handleToggleEnabled}
          />
        }
        label="Enable Promotions (show the Promotions panel inside each Quote)"
      />
      <Box sx={disabledStyle}>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" disabled={!featureOn} onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Promotion
        </Button>
        <Button variant="contained" color="secondary" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!featureOn || !selectedRow}>
          Edit Promotion
        </Button>
        <Button variant="outlined" color="error" onClick={handleDelete} disabled={!featureOn || !selectedRow}>
          Delete Promotion
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
        />
      )}

      <PromoDialog
        open={openDialog}
        data={editData}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
      </Box>{/* end disabled-when-OFF wrapper */}
    </Box>
  );
};

const PromoDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    available_to: '',
    visible: false,
    type: '',
    discount_type: '',
    discount: '',
    start_date: '',
    expiration_date: '',
    applies_to: ''
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        description: data.description || '',
        available_to: data.available_to || '',
        visible: data.visible ?? false,
        type: data.type || '',
        discount_type: data.discount_type || '',
        discount: data.discount || '',
        start_date: data.start_date || '',
        expiration_date: data.expiration_date || '',
        applies_to: data.applies_to || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        available_to: '',
        visible: false,
        type: '',
        discount_type: '',
        discount: '',
        start_date: '',
        expiration_date: '',
        applies_to: ''
      });
    }
  }, [data, open]);

  const handleSubmit = async () => {
    if (!formData.name || formData.name.trim() === '') {
      await alertC('Promotion Name is required.');
      return;
    }
    if (!formData.discount || formData.discount === '') {
      await alertC('Discount is required.');
      return;
    }
    const discountValue = Number(formData.discount);
    if (isNaN(discountValue) || discountValue < 0) {
      await alertC('Discount must be a positive number.');
      return;
    }
    if (formData.discount_type === '%' && discountValue > 100) {
      await alertC('Discount percentage cannot exceed 100%.');
      return;
    }
    if (!formData.discount_type) {
      await alertC('Discount Type is required.');
      return;
    }
    if (!formData.type) {
      await alertC('Type is required.');
      return;
    }
    if (formData.start_date && formData.expiration_date) {
      const startDate = new Date(formData.start_date);
      const expirationDate = new Date(formData.expiration_date);
      if (expirationDate <= startDate) {
        await alertC('Expiration Date must be after Start Date.');
        return;
      }
    }
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Promotion' : 'Add Promotion'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Promotion Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
          />
          <TextField
            label="Available To"
            value={formData.available_to}
            onChange={(e) => setFormData({ ...formData, available_to: e.target.value })}
            fullWidth
          />
          <FormControlLabel
            control={<Checkbox checked={formData.visible} onChange={(e) => setFormData({ ...formData, visible: e.target.checked })} />}
            label="Visible"
          />
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              label="Type"
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Discount Type</InputLabel>
            <Select
              value={formData.discount_type}
              label="Discount Type"
              onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
            >
              <MenuItem value="%">%</MenuItem>
              <MenuItem value="amount">Amount</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Discount"
            type="number"
            value={formData.discount}
            onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
            fullWidth
            inputProps={{ min: 0, max: formData.discount_type === '%' ? 100 : undefined, step: 0.01 }}
          />
          <TextField
            label="Start Date"
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Expiration Date"
            type="date"
            value={formData.expiration_date}
            onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Applies To"
            value={formData.applies_to}
            onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromotionsTab;
